/* ============================================================================
   /signup — Build 9 reskin.
   Same token system as Login.tsx — surface-raised card on surface-base.
   Neon card border and glow removed. Matches the rest of the app.
   ============================================================================ */
import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/components/AuthProvider";
import { Switch } from "@/components/ui/switch";
import { Logo } from "@/components/Logo";
import { NativeOAuthButtons } from "@/components/auth/NativeOAuthButtons";
import { BadgeCheck, ChevronRight, Crown } from "lucide-react";
import { BasicProCompareSheet } from "@/components/BasicProCompareSheet";
import { authErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/NexButton";
import { TextField } from "@/components/ui/NexTextField";

export default function Signup() {
  const { signup, signin, isSignedIn } = useAuth();
  const [, setLocation] = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The email already has an account and the password typed here is not its
  // password: the error says so, and the way forward (reset it) sits under it.
  const [accountExists, setAccountExists] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    if (isSignedIn) setLocation("/");
  }, [isSignedIn, setLocation]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setAccountExists(false);
    const trimmedEmail = email.trim();
    try {
      await signup({
        email: trimmedEmail,
        password,
        isUnder13: false,
        parentalConsentConfirmed: undefined,
      });
      setLocation("/");
    } catch (err: unknown) {
      let msg = authErrorMessage(err);
      if (/already|exists|in use|taken|registered/i.test(msg)) {
        // This page doubles as sign-in for an email that already has an
        // account: try the password they typed.
        try {
          await signin(trimmedEmail, password);
          setLocation("/");
          return;
        } catch {
          // It is not that account's password. Say what actually happened:
          // "Invalid email or password" on a page headed "Get started" reads
          // as the form refusing a new sign-up. (This used to print the
          // sign-in error raw, as `{"message":"Invalid email or password"}`.)
          msg = "This email already has a Nexus Dex account, and that password does not match it.";
          setAccountExists(true);
        }
      }
      setError(msg);
      setBusy(false);
    }
  };

  return (
    <div
      className="relative min-h-[100dvh] bg-[var(--surface-base)] flex justify-center px-4 sm:items-center"
      style={{
        paddingTop: "calc(var(--nx-safe-top) + 1.25rem)",
        paddingBottom: "calc(var(--nx-safe-bottom) + 1rem)",
      }}
    >
      <div
        className="relative w-full max-w-sm bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-[var(--radius-lg)] shadow-[var(--shadow-md)] p-7"
        data-testid="card-signup"
      >
        {/* Logo */}
        <div className="flex justify-center mb-5">
          <div style={{ width: "min(72px, 30vw)" }}>
            <Logo size={72} />
          </div>
        </div>

        {/* Eyebrow + title */}
        <p className="text-center text-xs font-medium text-[var(--text-tertiary)] tracking-wide uppercase mb-1">
          Get started
        </p>
        <h1
          className="text-2xl font-bold text-center text-[var(--text-primary)] leading-tight mb-2"
          data-testid="text-signup-title"
        >
          Your Dex starts here
        </h1>
        <p className="text-sm text-center text-[var(--text-secondary)] mb-6 leading-relaxed">
          One account, phone and web portal. Your trainer name and level come next.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <TextField
            label="Email"
            id="signup-email"
            data-testid="input-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <TextField
            label="Password"
            id="signup-password"
            data-testid="input-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            hint="8 characters or more"
            error={error ?? undefined}
          />

          {accountExists && (
            <div className="text-right -mt-2">
              <Link
                href="/auth/forgot-password"
                className="text-xs font-semibold text-[var(--accent-fill)] no-underline hover:opacity-80"
                data-testid="link-signup-forgot-password"
              >
                Forgot your password?
              </Link>
            </div>
          )}

          {/* Basic is free (owner ask, 2026-10-01): new trainers read this
              screen as the start of a payment flow. The two tiers drawn side
              by side, Basic lit as the one they are signing up for, and the
              full table one tap away. This replaced the "photo library only"
              note, which was about the profile screenshot (gone entirely
              since 2026-10-02: the trainer types their name and level). */}
          <div
            className="rounded-[var(--radius-md)] border border-[var(--border-subtle)] overflow-hidden"
            data-testid="signup-tiers"
          >
            <div className="grid grid-cols-2">
              <div className="p-3 bg-[var(--accent-subtle)]" data-testid="signup-tier-basic">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] inline-flex items-center gap-1">
                  <BadgeCheck className="w-3 h-3 text-[var(--accent-fill)]" aria-hidden />
                  Basic
                </p>
                <p className="text-lg font-bold leading-tight text-[var(--accent-fill)]">Free</p>
                <p className="text-[10.5px] leading-snug text-[var(--text-secondary)]">No payment to sign up</p>
              </div>
              <div className="p-3 border-l border-[var(--border-subtle)]" data-testid="signup-tier-pro">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] inline-flex items-center gap-1">
                  <Crown className="w-3 h-3" aria-hidden />
                  Pro
                </p>
                <p className="text-lg font-bold leading-tight text-[var(--text-primary)]">Optional</p>
                <p className="text-[10.5px] leading-snug text-[var(--text-secondary)]">Upgrade any time</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCompareOpen(true)}
              className="w-full py-2 text-xs font-semibold text-[var(--accent-fill)] border-t border-[var(--border-subtle)] inline-flex items-center justify-center gap-1 hover:opacity-80"
              data-testid="button-signup-compare"
            >
              Compare Basic and Pro
              <ChevronRight className="w-3.5 h-3.5" aria-hidden />
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={busy}
            className="w-full"
            data-testid="button-signup-submit"
          >
            {busy ? "Continuing…" : "Continue"}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 border-t border-[var(--border-subtle)]" />
          <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--text-tertiary)]">
            or
          </span>
          <div className="flex-1 border-t border-[var(--border-subtle)]" />
        </div>

        {/* OAuth, below the email form (2026-09-11): the email and password
            fields sit high enough to stay clear of the keyboard, whichever
            element owns the page's scroll. */}
        <NativeOAuthButtons onSuccess={() => setLocation("/")} />

        <BasicProCompareSheet open={compareOpen} onClose={() => setCompareOpen(false)} />

        <p className="text-xs mt-5 text-center text-[var(--text-tertiary)]">
          Already have an account?{" "}
          <Link
            href="/login"
            className="font-semibold text-[var(--accent-fill)] no-underline hover:opacity-80"
            data-testid="link-signin"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
